/* eslint-disable camelcase */
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { countries } from "countries-list";
import content from "../../constants/content";
import { Modal } from "../../common-components/modal";
import {
  AmountContainer, Body, BodyContainer, CardBodyContainer, CardOptionContainer, Checkbox, CheckboxWrapper,
  CheckMark, Column, CreditCardContainer, Details, Divide, DropDownContainer, DropdownContent,
  DropdownHeaders, DropdownWraps, Flex, Image, ImageContainer, ImageWrap, IndividualLabel,
  Labels, LabelWrap, OrderSummary, PaymentFooter, PlanAndTeamContainer, PlanBadge, PlanDetailsContainer,
  ProratedContainer, Regularprice, SummaryHeaderContainer, TopWrap
} from "./payment-component";
import DropdownLabel from "../../common-components/DropdownLabel";
import Typography from "../../common-components/Typography";
import { font } from "../../constants/font";
import { PLAN } from "../../constants";
import { STATIC_PATH } from "../../constants/config";
import PaymentInput from "../../common-components/PaymentInput";
import Action from "../../common-components/Action";
import { setWorkspaceTextFocus } from "../../redux/actions/appUtils";
import BackdropLoader from "../../common-components/BackdropLoader";
import useBraintree from "../../helper/hooks/useBraintree";
import useEscape from "../../helper/hooks/useEscape";
import useMobileDetection from "../../helper/hooks/useMobileDetection";
import PaymentTooltip from "./PaymentTooltip";


const { PAY, TOTAL, CREDIT_CARD } = content;

const PaymentModal = ({
  isShow,
  onAction,
  planDetails,
  onClose,
  teamCount,
  monthlyAmount = 0,
  yearlyAmount = 0,
  selectPlan,
  getBillingCycle,
  discountDetails,
  isValidCoupon,
  regularPrice,
  defaultCycle = "yearly",
  loadingDisable = false,
  isCardUpdate = false,
  countryPin,
  changePaymentCurrency,
  paymentCurrency,
  paymentMode,
  countryName,
  refundAmount
}) => {
  const { createClientToken, convertPayment, changeCard, verifyThreeDSecure } = useBraintree();
  const [isCardInputOpen, setIsCardInputOpen] = useState(false);
  const [isPaypal, setIsPaypal] = useState(false);
  const [isRazorpay, setIsRazorpay] = useState(false);
  const [planCycleName, setPlanCycleName] = useState(defaultCycle);
  const [countryCode, setCountryCode] = useState(countryPin);
  const [convertedAmount, setConvertedAmount] = useState("");
  const [isNewCardUpdate, setIsNewCardUpdate] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [oldNonce, setOldNonce] = useState("");
  const [binData, setBinData] = useState("");
  const [brand, setBrand] = useState("none");
  const [cummulativePrice, setCummulativePrice] = useState(monthlyAmount);
  const [regularAmount, setRegularAmount] = useState(0);
  const [totalPayableAmount, setTotalPayableAmount] = useState(0);
  const [disCountedAmount, setDisCountedAmount] = useState(0);
  const dispatch = useDispatch();
  const contextDetails = useSelector((state) => state.app.get("planDetails"));
  const [isCouponApplied, setCouponApplied] = useState("");
  const [convertCountry, setConvertCountry] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(countryName);
  const { current_plan, current_cycle } = contextDetails || {};
  const { plan } = useSelector((state) => state.userDetails);
  const isMobile = useMobileDetection();
  const [planOptions, setPlanOptions] = useState([
    { value: "Monthly", label: "Yearly" }
  ]);

  const renewalPeriod = planCycleName?.toLowerCase() === "yearly" ? "year" : "month";

  const handleTenureClick = (cycle) => {
    let total;
    if (planCycleName !== cycle) {
      setPlanCycleName(cycle);
      getBillingCycle(cycle);
    }
    if (cycle === "Yearly") {
      total = yearlyAmount * teamCount
      setCummulativePrice(yearlyAmount);
    } else {
      total = monthlyAmount * teamCount
      setCummulativePrice(monthlyAmount);
    }
    setTotalPayableAmount(total);
  };

  // Handle change when the user selects a plan from DropdownLabel
  const handleChange = (selectedValue) => {
    setPlanCycleName(selectedValue);
    handleTenureClick(selectedValue);
  };

  // Dynamically set the style based on the selected plan
  const getPlanStyle = (plan) => {
    switch (plan) {
      case "starter":
        return { backgroundColor: "#FFC300", color: "white" };
      case "team":
        return { backgroundColor: "#00baff", color: "white" };
      default:
        return {};
    }
  };

  useEffect(() => {
    if (current_plan === PLAN.LITE || plan === PLAN.LITE || (current_plan === PLAN.STARTER && current_cycle === "monthly" && selectPlan === PLAN.TEAM)) {
      setPlanOptions([
        { value: "Monthly", label: "Monthly" },
        { value: "Yearly", label: "Yearly" }
      ])
    } else {
      setPlanOptions([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_plan, plan, selectPlan]);

  const handleClose = (e) => {
    onClose();
    if (showLoader) {
      e.stopPropagation();
    } else {
      setIsNewCardUpdate(false);
    }
  };

  const countryOptions = useMemo(() => {
    return Object.keys(countries).map((code) => ({
      value: code,
      label: countries[code],
    }));
  }, []);

  // Transform options to desired format
  const transformedOptions = useMemo(() => {
    return countryOptions.map((option) => ({
      label: option.label.name,
      value: option.value,
    }));
  }, [countryOptions]);

  const cardOnClick = () => {
    if (!isCardInputOpen && !paymentMode) {
      setIsCardInputOpen(true);
      setIsPaypal(false);
      setIsRazorpay(false);
      setIsNewCardUpdate(true);
      changePaymentCurrency("USD");
    } else if (paymentMode === "bt") {
      setIsNewCardUpdate(false);
      setIsCardInputOpen(true);
    }
  };

  const handlePaypalClick = () => {
    if (!isPaypal) {
      setIsCardInputOpen(false);
      setIsPaypal(true);
      setIsRazorpay(false);
      setIsNewCardUpdate(false);
      changePaymentCurrency("USD");
    }
  };

  const handleRazorpayClick = () => {
    if (!isRazorpay) {
      setIsRazorpay(true);
      setIsCardInputOpen(false);
      setIsPaypal(false);
      setIsNewCardUpdate(false);
      changePaymentCurrency("IN");
    }
  };

  useEffect(() => {
    if (discountDetails && planCycleName) {
      const discountAmount = planCycleName === "yearly" ? discountDetails.yearlyDiscount : discountDetails.monthlyDiscount;
      setDisCountedAmount(discountAmount || 0);
    }
  }, [discountDetails, planCycleName]);
  const handlePayment = (payAmount) => {
    setShowLoader(true);
    if (isPaypal) {
      onAction("paypal");
    } else if (isRazorpay) {
      const plans = { selectPlan, planCycleName }
      onAction("razorpay", plans);
    } else if (isNewCardUpdate || brand === "none") {
      changeCard(payAmount).then((res) => {
        onAction("changeCard", res.nonce, isCouponApplied);
      }).catch(() => {
        setShowLoader(false);
      })
    } else {
      // Existing card verification
      const amount = payAmount;
      verifyThreeDSecure(oldNonce, amount, binData).then((res) => {
        onAction("card", res.nonce, isCouponApplied);
      }).catch(() => {
        setShowLoader(false);
      })
    }
  };

  const handleCountryOnChange = (value) => {
    setConvertCountry(value);
  };

  useEffect(() => {
    if (isValidCoupon) {
      setCouponApplied(isValidCoupon);
    }
  }, [isValidCoupon])

  useEffect(() => {
    if (planCycleName === "yearly") {
      const total = yearlyAmount * teamCount
      setTotalPayableAmount(total);
      setCummulativePrice(yearlyAmount);
    } else if (planCycleName === "monthly") {
      const total = monthlyAmount * teamCount
      setTotalPayableAmount(total);
      setCummulativePrice(monthlyAmount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyAmount]);

  useEffect(() => {
    if (totalPayableAmount && convertCountry) {
      convertPayment(totalPayableAmount, convertCountry).then((res) => {
        if (res.success) {
          setConvertedAmount(res.amount);
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPayableAmount, convertCountry]);

  useEffect(() => {
    if (paymentMode === "paypal") {
      handlePaypalClick();
    } else if (paymentMode === "bt") {
      cardOnClick();
    } else if (paymentMode === "razorpay") {
      handleRazorpayClick();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMode]);

  const { addListener, removeListerner } = useEscape(handleClose);

  useEffect(() => {
    try {
      if (isShow) {
        if (countryPin) {
          setCountryCode(countryPin);
          setSelectedCountry(countryName);
        }
        if (countryPin !== "IN") {
          createClientToken();
        }
        if (!paymentMode || isCardUpdate) {
          if (countryPin === "IN") {
            handleRazorpayClick();
          } else {
            cardOnClick();
          }
        }
        addListener();
      }

    } catch (error) {
      console.error("Error in PaymentModal useEffect:", error);
    }

    return () => {
      removeListerner();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow]);

  useEffect(() => {
    if (planDetails) {
      setOldNonce(planDetails.oldNonce);
      setBinData(planDetails.binData);
      setBrand(planDetails.brand);
    }
  }, [planDetails]);

  useEffect(() => {
    if (isCardInputOpen) {
      createClientToken();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCardInputOpen]);

  useEffect(() => {
    if (loadingDisable) {
      setShowLoader(false);
    }
  }, [loadingDisable])

  useEffect(() => {
    if (regularPrice && selectPlan && planCycleName) {
      const planCycle = planCycleName.toLowerCase()
      const amount = regularPrice[selectPlan][planCycle];
      setRegularAmount(amount);
    }
  }, [regularPrice, selectPlan, planCycleName]);

  useEffect(() => {
    let lastTouchEnd = 0;

    const handleTouchEnd = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        e.preventDefault();
      }
    };

    const handleGestureStart = (e) => {
      e.preventDefault();
    };

    document.addEventListener('touchend', handleTouchEnd, false);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('gesturestart', handleGestureStart);

    return () => {
      document.removeEventListener('touchend', handleTouchEnd, false);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('gesturestart', handleGestureStart);
    };
  }, []);

  useEffect(() => {
    if (!paymentMode && countryPin === "IN") {
      handleRazorpayClick();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryPin]);

  useEffect(() => {
    if (paymentCurrency) {
      setCountryCode(paymentCurrency)
    }
  }, [paymentCurrency])

  return (
    <>
      <Modal
        onClose={(e) => handleClose(e)}
        width={"1012px"}
        height={"600px"}
        mobWidth="auto"
        showModal={isShow}
        className="payment--modal"
        overflow="hidden"
        smOverflow={"scroll"}
        smHeight={"95%"}
      >
        <Flex>
          <Column style={{ width: "464px", padding: "40px" }}>
            <SummaryHeaderContainer>
              <OrderSummary>
                <Action
                  background={"#FFFFFF"}
                  borderRadius="8px"
                  className="pay"
                  height="28px"
                  width="28px"
                  borderColor={" #E3E4E7"}
                  borderWidth={"1px"}
                  boxShadow={"0px 1px 3px #00000029"}
                  showHoverColor={false}
                  marginBottom={"0px"}
                  marginLeft={"0px"}
                  marginRight={"0px"}
                  marginTop={"0px"}

                >
                  <Image
                    src={`${STATIC_PATH}back-arrow.svg`}
                    width="20px"
                    height="20px"
                    alt="back"
                    className="back-button"
                    onClick={(e) => handleClose(e)}
                  />
                </Action>
                <Typography
                  content={content.ORDER_SUMMARY}
                  color={"#31304A"}
                  font={font.bold_20_16}
                  display={"block"}
                  align={"center"}
                  margin={"0px 0px 0px"}
                  height={"28px"}
                  padding={"6px"}
                />
              </OrderSummary>
              {
                (!isMobile && countryCode !== "IN") && <DropDownContainer>
                  <DropdownLabel
                    labelText={selectedCountry}
                    labelColor="#424256"
                    border={"1px solid transparent"}
                    borderActive={"1px solid transparent"}
                    selectedHeight="0px"
                    padding={"0px 0px"}
                    position={"static"}
                    fontStyle={font.normal}
                    optionWidth="auto"
                    options={transformedOptions}
                    optionsHeight={"360px"}
                    onChange={(e) => handleCountryOnChange(e)}
                    margin={"0px"}
                    optionHeight={"360px"}
                    maxWidth={"136px"}
                    defaultValue={countryPin}
                  />
                </DropDownContainer>
              }
            </SummaryHeaderContainer>
            <PlanAndTeamContainer>
              <Details>
                <PlanDetailsContainer>
                  <Typography
                    content="Plan:"
                    color={"#54536E"}
                    beforeIcon={`${STATIC_PATH}vm-stars.svg`}
                    font={font.normalMicroLarge_12}
                  />
                  <PlanBadge
                    style={getPlanStyle(selectPlan)}
                    className="plan-badge"
                  >
                    <img
                      src={`${STATIC_PATH}sm-star.svg`}
                      alt="star icon"
                      className="plan-icon"
                    />
                    <Typography
                      content={selectPlan}
                      font={font.normal_12_19}
                      color={"#ffffff"}
                      textStyles={{ textTransform: "capitalize" }}
                    />
                  </PlanBadge>
                </PlanDetailsContainer>
                {
                  planOptions.length > 1 && <DropdownLabel
                    width="fit-content"
                    labelText={planCycleName}
                    labelColor="#4A4F66"
                    labelMargin="20px 0px 0px"
                    border={"1px solid transparent"}
                    borderActive={"1px solid transparent"}
                    afterImage={`${STATIC_PATH}orange-arrow.svg`}
                    height="0px"
                    margin={"0px"}
                    padding={"0px"}
                    position={"static"}
                    fontStyle={font.normal_600_22}
                    defaultValue={planCycleName.charAt(0).toUpperCase() + planCycleName.slice(1)}
                    optionWidth="150px"
                    options={planOptions}
                    optionsHeight={"auto"}
                    onChange={handleChange}
                    right={isMobile ? "0px" : "54%"}
                  />
                }
              </Details>
              <Details>
                <Typography
                  innerContent={`Team Members:  ${teamCount}`}
                  color={"#54536E"}
                  beforeIcon={`${STATIC_PATH}team-members.svg`}
                  font={font.normalMicroLarge_12}
                />
                <Typography
                  innerContent={`${teamCount} x ${countryCode === "IN" ? "₹" : "$"}${regularAmount}`}
                  color={"#54536E"}
                  padding={"2px 0px"}
                  font={font.normalMicroLarge_12}
                />
              </Details>
              {
                ((paymentMode === "bt" || (paymentMode === "paypal" && planCycleName === "monthly")) && regularAmount > cummulativePrice) &&
                <Details>
                  <Typography
                    className={"prorate"}
                    content={content.PRORATED_PRICE}
                    font={font.normalMicroLarge_12}
                    color={"#54536E"}
                    beforeIcon={`${STATIC_PATH}icon-pro-rated-price.svg`}
                    beforeIconSize={"32px"}
                  />
                  {
                    !isMobile && <PaymentTooltip
                      content={content.PRORATED_TOOLTIP}
                      right={"22%"}
                      width={"210px"}
                    >
                      <img src={`${STATIC_PATH}tool-tip.svg`} alt="" />
                    </PaymentTooltip>
                  }
                  <Typography
                    innerContent={`-${(countryCode === "IN" && isRazorpay) ? "₹" : "$"}${Number((regularAmount - cummulativePrice).toFixed(2)) * teamCount}`}
                    color={"#54536E"}
                    padding={"2px 0px"}
                    font={font.normalMicroLarge_12}
                  />
                </Details>
              }
            </PlanAndTeamContainer>
            <Regularprice>
              {
                <ProratedContainer>
                  <Typography
                    content={"Subtotal"}
                    font={font.normalMicroLarge_12}
                    color={"#54536E"}
                    beforeIcon={`${STATIC_PATH}regular.svg`}
                  />
                  <Typography
                    innerContent={`${countryCode === "IN" ? "₹" : "$"} ${countryCode === "IN" ? regularAmount : totalPayableAmount}`}
                    color={"#54536E"}
                    padding={"2px 0px"}
                    font={font.normalMicroLarge_12}
                  />
                </ProratedContainer>
              }
            </Regularprice>
            <TopWrap>
              <Typography
                content={TOTAL}
                color="#32304A"
                font={font.normal_600_12}
                beforeIcon={`${STATIC_PATH}total-bill.svg`}
              />

              <AmountContainer>
                {(disCountedAmount > 0) &&
                  <Typography
                    innerContent={`${countryCode === "IN" ? "₹" : "$"}${totalPayableAmount - disCountedAmount}`}
                    color={"#141212"}
                    font={font.normalBold_22}
                    padding={"0px 10px"}
                  />
                }
                <Typography
                  innerContent={`${countryCode === "IN" ? "₹" : "$"} ${countryCode === "IN" ? (regularAmount * teamCount) : totalPayableAmount - disCountedAmount}`}
                  color={disCountedAmount > 0 ? "#FF2859" : "#313345"}
                  font={disCountedAmount > 0 ? font.normal_bold_20 : font.normal_bold_20}
                  padding={"2px 0px"}
                  style={{ textDecoration: disCountedAmount > 0 ? 'line-through' : "" }}
                />
              </AmountContainer>
            </TopWrap>
            {
              (convertedAmount && !isMobile) && <Typography
                innerContent={`Approx: ~ ${convertedAmount}`}
                font={font.normal_normal_14_12}
                display={"flex"}
                color={"#7B7E8C"}
                style={{ justifyContent: "end" }}
                className="convert-value"
                padding={"4px 0px 0px 0px"}
              />
            }
          </Column>
          <Column style={{ width: "548px" }}>
            <Body background={"#f8f8f8"}>
              <BodyContainer>
                <CardBodyContainer>
                  <Typography
                    content={"Payment Method"}
                    font={font.bold_20_16}
                    color={"#313345"}
                    display={"block"}
                    padding={'4px 0px 16px'}
                  />
                  <CardOptionContainer>
                    <Typography
                      content={"Indian Cards (charged in Rupees)"}
                      font={font.normalMicroLarge}
                      color={"#7B7E8C"}
                    />
                    <DropdownWraps width={"auto"} isDisable={paymentMode === "bt" || paymentMode === "paypal" || countryPin !== "IN"} >
                      <DropdownHeaders onClick={handleRazorpayClick} isDisable={paymentMode === "bt" || paymentMode === "paypal" || countryPin !== "IN"}>
                        <Labels>
                          <CheckboxWrapper>
                            <Checkbox
                              type="checkbox"
                              checked={isRazorpay}
                              onChange={handleRazorpayClick}

                            />
                            <CheckMark checked={isRazorpay} />
                          </CheckboxWrapper>
                          <Typography
                            content={"UPI / Card"}
                            color={"#31304A"}
                            font={font.normal_14_12}
                            cursor={(paymentMode === "bt" || countryPin !== "IN" || paymentMode === "paypal") ? "not-allowed" : "pointer"}
                            padding={"2px 0px"}
                            beforeIconSize={"18px"}
                            beforeIcon={`${STATIC_PATH}upi-icon.svg`}
                          />
                        </Labels>
                        <ImageWrap>
                          <Image
                            src={`${STATIC_PATH}upi.svg`}
                            alt="toggle-icon"
                            style={{ height: "25px" }}
                          />
                          <Image
                            src={`${STATIC_PATH}mastercard.svg`}
                            alt="toggle-icon"
                          />
                          <Image src={`${STATIC_PATH}visa.svg`} alt="toggle-icon" />
                        </ImageWrap>
                      </DropdownHeaders>
                    </DropdownWraps>
                  </CardOptionContainer>
                  <CardOptionContainer>
                    <Typography
                      content={"International Cards (charged in USD)"}
                      font={font.normalMicroLarge}
                      color={"#7B7E8C"}
                      display={"block"}
                      padding={'16px 0px 0px 0px'}
                    />
                    <CreditCardContainer>
                      <DropdownWraps width={"auto"} isDisable={paymentMode === "paypal" || paymentMode === "razorpay"}>
                        <DropdownHeaders
                          isCardUpdate={isCardInputOpen}
                          isDisable={paymentMode === "paypal" || paymentMode === "razorpay"}
                          onClick={cardOnClick}
                        >
                          <Labels onClick={cardOnClick}>
                            <CheckboxWrapper>
                              <Checkbox
                                type="checkbox"
                                checked={isCardInputOpen}
                                onChange={cardOnClick}
                              />
                              <Divide>
                                <CheckMark checked={isCardInputOpen} />
                              </Divide>
                            </CheckboxWrapper>
                            <Typography
                              content={isMobile ? "Credit or Debit card" : CREDIT_CARD}
                              color={"#31304A"}
                              font={font.normal_14_12}
                              cursor="pointer"
                              padding={"4px 0px"}
                              beforeIcon={`${STATIC_PATH}cc-icon.svg`}
                              enableTrim={false}
                            />
                          </Labels>
                          <ImageWrap>
                            <Image
                              src={`${STATIC_PATH}american-express.svg`}
                              alt="toggle-icon"
                            />
                            <Image
                              src={`${STATIC_PATH}mastercard.svg`}
                              alt="toggle-icon"
                            />
                            <Image src={`${STATIC_PATH}visa.svg`} alt="toggle-icon" />
                          </ImageWrap>
                        </DropdownHeaders>
                        <DropdownContent isVisible={(isPaypal || isRazorpay || paymentMode === "bt") ? false : isNewCardUpdate}>
                          <IndividualLabel>
                            <Typography
                              content={"Card Number"}
                              font={font.normal_medium_12_22}
                              color={"#4A4F66"}
                              display={"block"}
                            />
                            <PaymentInput
                              id="card-number-editor"
                              label="Card Number"
                              top="52px"
                              labelColor={"#8A9AA3"}
                              placeholder="Add Card Number"
                              paddingLeft={true}
                              onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                              onBlur={() => dispatch(setWorkspaceTextFocus(true))}
                            />
                          </IndividualLabel>
                          <LabelWrap>
                            <IndividualLabel>
                              <Typography
                                content={"Expiration Date"}
                                font={font.normal_medium_12_22}
                                color={"#4A4F66"}
                                display={"block"}
                              />
                              <PaymentInput
                                id="expiration-date-editor"
                                width={isMobile ? "100%" : "132px"}
                                labelColor={"#8A9AA3"}
                                label="Expiration Date (MM/YY)"
                                placeholder="MM/YY"
                                top="52px"
                                paddingLeft={true}
                                onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                                onBlur={() => dispatch(setWorkspaceTextFocus(true))}
                              />
                            </IndividualLabel>
                            <IndividualLabel>
                              <Typography
                                content={"Security code"}
                                font={font.normal_medium_12_22}
                                color={"#4A4F66"}
                                display={"block"}
                              />
                              <PaymentInput
                                id="cvv-editor"
                                width={isMobile ? "100%" : "132px"}
                                labelColor={"#8A9AA3"}
                                label="Security code"
                                top="52px"
                                placeholder="CVV"
                                paddingLeft={true}
                                onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                                onBlur={() => dispatch(setWorkspaceTextFocus(true))}
                              />
                            </IndividualLabel>
                            <IndividualLabel>
                              <Typography
                                content={"Zip Code"}
                                font={font.normal_medium_12_22}
                                color={"#4A4F66"}
                                display={"block"}
                              />
                              <PaymentInput
                                id="zipcode-editor"
                                width={isMobile ? "100%" : "132px"}
                                top="52px"
                                label="Zip Code"
                                labelColor={"#8A9AA3"}
                                placeholder="Add Zipcode"
                                paddingLeft={true}
                                onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                                onBlur={() => dispatch(setWorkspaceTextFocus(true))}
                              />
                            </IndividualLabel>
                          </LabelWrap>
                        </DropdownContent>
                      </DropdownWraps>

                      <DropdownWraps width={"auto"} isDisable={paymentMode === "bt" || paymentMode === "razorpay"}>
                        <DropdownHeaders onClick={handlePaypalClick} isDisable={paymentMode === "bt" || paymentMode === "razorpay"}>
                          <Labels>
                            <CheckboxWrapper>
                              <Checkbox
                                type="checkbox"
                                checked={isPaypal}
                                onChange={handlePaypalClick}
                              />
                              <CheckMark checked={isPaypal} />
                            </CheckboxWrapper>
                            <Typography
                              content={content.PAYPAL}
                              color={"#31304A"}
                              font={font.normal_14_12}
                              cursor={paymentMode === "bt" ? "not-allowed" : "pointer"}
                              padding={"2px 0px"}
                            />
                          </Labels>
                          <img src={`${STATIC_PATH}card-icon.svg`} alt="toggle-icon" />
                        </DropdownHeaders>
                      </DropdownWraps>
                      {
                        (refundAmount > 0 && ((planCycleName === "yearly" && current_cycle === "monthly") || paymentMode === "razorpay")) &&
                          <Typography
                            content={`Remaining ${countryCode === "IN" ? "₹" : "$"}${refundAmount} from the current subscription will be refunded within 7 working days`}
                            className={"subscription-message"}
                            color={"#ff0000"}
                            font={font.normal_11_15}
                            display={"inline-block"}
                            align={"center"}
                            padding={"6px 0px 0px 0px"}
                          />
                      }
                      <Typography
                        content={`Your subscription will auto renew every ${renewalPeriod}. You can unsubscribe anytime via My Plan on dashboard.`}
                        color={"#7B7E8C"}
                        font={font.normal_11_15}
                        display={"inline-block"}
                        align={"center"}
                        enableTrim={false}

                      />
                    </CreditCardContainer>
                  </CardOptionContainer>
                </CardBodyContainer>
                <PaymentFooter>
                  <Action
                    text={PAY}
                    background={"transparent linear-gradient(95deg, #FF781A 0%, #F55322 100%) 0% 0% no-repeat padding-box"}
                    font={font.largeBold_14}
                    borderRadius="8px"
                    className="pay"
                    customBeforeIcon={`${STATIC_PATH}pay-icon.svg`}
                    height="40px"
                    onClick={() => handlePayment(totalPayableAmount - disCountedAmount)}
                    marginBottom={"20px"}
                    marginTop={"0px"}
                  />
                  <Typography
                    content={content.PAYMENTS_SAFE}
                    color={"#7B7E8C"}
                    font={font.normal_11_15}
                    padding={"0px 50px 16px"}
                    width={"100%"}
                    enableTrim={false}
                    align={"center"}
                  />
                  <Typography
                    content={content.TRUSTED}
                    color={"#7B7E8C"}
                    padding="16px 0px 20px"
                    font={font.normalMicro_16}
                    width={"100%"}
                    enableTrim={false}
                    align={"center"}
                    style={{ borderTop: "1px solid #DCE0E3" }}
                  />
                  <ImageContainer>
                    <Image src={`${STATIC_PATH}amazon.svg`} alt="amazon" />
                    <Image src={`${STATIC_PATH}hp.svg`} alt="hp" />
                    <Image src={`${STATIC_PATH}renault.svg`} alt="renault" />
                    <Image src={`${STATIC_PATH}philips.svg`} alt="philips" />
                  </ImageContainer>
                </PaymentFooter>
              </BodyContainer>
            </Body>
          </Column>
        </Flex>
      </Modal>
      {showLoader && <BackdropLoader content={"Please wait while the payment is processing..."} />}
    </>
  );
};

PaymentModal.propTypes = {
  isShow: PropTypes.bool,
  onAction: PropTypes.func,
  planDetails: PropTypes.object,
  onClose: PropTypes.func,
  teamCount: PropTypes.number,
  monthlyAmount: PropTypes.number,
  yearlyAmount: PropTypes.number,
  selectPlan: PropTypes.string,
  getBillingCycle: PropTypes.func,
  loadingDisable: PropTypes.bool,
  paymentMode: PropTypes.string,
  isCardUpdate: PropTypes.bool,
  discountDetails: PropTypes.object,
  isValidCoupon: PropTypes.string,
  defaultCycle: PropTypes.string,
  regularPrice: PropTypes.array,
  refundAmount: PropTypes.number,
  countryPin: PropTypes.string,
  changePaymentCurrency: PropTypes.func,
  paymentCurrency: PropTypes.string,
  countryName: PropTypes.string,
};

export default PaymentModal;
